export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

declare global {
  interface Window {
    /** Set by the Tauri shell before the app bundle runs. Empty outside the shell. */
    __EDCC_API_BASE__?: string;
  }
}

function withApiBase(path: string): string {
  if (typeof window === "undefined") return path;
  const base = window.__EDCC_API_BASE__;
  if (!base || !path.startsWith("/")) return path;
  return `${base.replace(/\/+$/, "")}${path}`;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  const qs = params.toString();
  return qs.length > 0 ? `${path}?${qs}` : path;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers["content-type"] = "application/json";
  if (method !== "GET") headers["x-edcc-request"] = "1";
  let response: Response;
  try {
    response = await fetch(withApiBase(buildUrl(path, options.query)), {
      method,
      headers,
      credentials: "same-origin",
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (error) {
    throw new ApiError(0, "network-error", `Could not reach the EDCC server (${(error as Error).message}).`);
  }
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }
  if (!response.ok) {
    const body = parsed as ApiErrorBody | null;
    throw new ApiError(
      response.status,
      body?.error?.code ?? "unknown",
      body?.error?.message ?? `Request failed with HTTP ${response.status}`,
      body?.error?.details,
    );
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions["query"], signal?: AbortSignal) =>
    apiRequest<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: "PATCH", body }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: "DELETE" }),
};

/** Reads an SSE response body and invokes `onEvent` per `data:` payload. */
export async function readSseStream(
  path: string,
  body: unknown,
  onEvent: (payload: unknown) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(withApiBase(path), {
    method: "POST",
    headers: { "content-type": "application/json", "x-edcc-request": "1" },
    credentials: "same-origin",
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, "stream-error", `AI request failed (HTTP ${response.status}): ${text.slice(0, 300)}`);
  }
  if (!response.body) throw new ApiError(0, "stream-error", "The server did not return a stream.");
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() ?? "";
    for (const part of parts) {
      const dataLine = part
        .split("\n")
        .find((line) => line.startsWith("data: "));
      if (!dataLine) continue;
      try {
        onEvent(JSON.parse(dataLine.slice(6)));
      } catch {
        // ignore malformed SSE payloads
      }
    }
  }
}
